import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PdfFitPreview from '../../../components/common/PdfFitPreview';
import PdfThumbnailPreview from '../../../components/common/PdfThumbnailPreview';
import { THEME_COLORS } from '../../../shared/constants/theme';
import {
  formatWhatsAppErrorMessage,
  formatWhatsAppSuccessMessage,
} from '../../../utils/whatsappMessaging';
import {
  downloadHallBookingConfirmation,
  downloadHallBookingInvoice,
  fetchHallBookingConfirmationPdfBlob,
  fetchHallBookingInvoicePdfBlob,
  sendHallBookingDocuments,
} from '../api';

const DOC_CARD_WIDTH = 200;
const A4_PREVIEW_ASPECT = '210 / 297';

function DocumentPreviewCard({
  docType,
  label,
  Icon,
  selected,
  focused,
  pdfUrl,
  loading,
  isDark,
  accent,
  onSelect,
  onExpand,
}) {
  const selectedBorder = isDark ? alpha('#facc15', 0.85) : alpha('#f59e0b', 0.72);
  const idleBorder =
    docType === 'voucher'
      ? (isDark ? alpha('#67e8f9', 0.35) : alpha('#0ea5e9', 0.22))
      : (isDark ? alpha('#5eead4', 0.35) : alpha('#0f766e', 0.24));

  return (
    <Paper
      elevation={0}
      onClick={onSelect}
      sx={{
        flex: { xs: '1 1 0', sm: `0 0 ${DOC_CARD_WIDTH}px` },
        width: { xs: '100%', sm: DOC_CARD_WIDTH },
        overflow: 'hidden',
        cursor: 'pointer',
        borderRadius: 2,
        border: `1.5px solid ${selected ? selectedBorder : idleBorder}`,
        boxShadow: selected
          ? `0 8px 18px ${alpha('#f59e0b', isDark ? 0.24 : 0.18)}`
          : focused
            ? `0 0 0 1px ${alpha(accent, 0.35)}`
            : 'none',
        transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
        bgcolor: selected
          ? (isDark ? alpha('#facc15', 0.14) : alpha('#fde68a', 0.52))
          : (isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : alpha('#fff', 0.96)),
        '&:hover': {
          transform: 'translateY(-1px)',
          borderColor: selected ? selectedBorder : (isDark ? alpha('#5eead4', 0.6) : alpha('#0f766e', 0.4)),
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: A4_PREVIEW_ASPECT,
          borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
          bgcolor: isDark ? '#0b1220' : '#fff',
        }}
      >
        {loading && !pdfUrl ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
            }}
          >
            <CircularProgress size={22} sx={{ color: accent }} />
          </Stack>
        ) : (
          <PdfThumbnailPreview pdfUrl={pdfUrl} accentColor={accent} isDark={isDark} fill />
        )}
        {pdfUrl ? (
          <Tooltip title="View full size">
            <IconButton
              size="small"
              aria-label={`View ${label} full size`}
              onClick={(event) => {
                event.stopPropagation();
                onExpand?.(docType);
              }}
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                width: 28,
                height: 28,
                bgcolor: alpha('#000', 0.55),
                color: '#fff',
                '&:hover': { bgcolor: alpha('#000', 0.72) },
              }}
            >
              <OpenInFullIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Icon
          sx={{
            fontSize: 16,
            color: selected
              ? (isDark ? '#fde68a' : '#b45309')
              : (docType === 'voucher'
                ? (isDark ? '#7dd3fc' : '#0369a1')
                : (isDark ? '#5eead4' : '#0f766e')),
          }}
        />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.8125rem',
            lineHeight: 1.25,
            color: selected
              ? (isDark ? '#fde68a' : '#92400e')
              : (isDark ? '#e2e8f0' : '#0f172a'),
          }}
          noWrap
        >
          {label}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function HallEventDocumentsPreviewPanel({
  bookingId,
  bookingCode,
  guestName = null,
  isDark,
  accent = '#4ce1b6',
  docLoading,
  setDocLoading,
  canSendEmail = false,
  canSendWhatsApp = false,
  onNotice,
  voucherLabel = 'Voucher',
  invoiceLabel = 'Invoice',
}) {
  const [selectedDownloadDocs, setSelectedDownloadDocs] = useState({
    voucher: false,
    invoice: true,
  });
  const [previewDocFocus, setPreviewDocFocus] = useState('invoice');
  const [voucherPdfUrl, setVoucherPdfUrl] = useState(null);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [expandedDocType, setExpandedDocType] = useState(null);
  const voucherUrlRef = useRef(null);
  const invoiceUrlRef = useRef(null);

  const activePreviewDocType = useMemo(() => {
    if (previewDocFocus === 'voucher' && selectedDownloadDocs.voucher) return 'voucher';
    if (previewDocFocus === 'invoice' && selectedDownloadDocs.invoice) return 'invoice';
    if (selectedDownloadDocs.invoice) return 'invoice';
    if (selectedDownloadDocs.voucher) return 'voucher';
    return null;
  }, [previewDocFocus, selectedDownloadDocs]);

  const revokeVoucherUrl = useCallback(() => {
    if (voucherUrlRef.current) {
      URL.revokeObjectURL(voucherUrlRef.current);
      voucherUrlRef.current = null;
    }
  }, []);

  const revokeInvoiceUrl = useCallback(() => {
    if (invoiceUrlRef.current) {
      URL.revokeObjectURL(invoiceUrlRef.current);
      invoiceUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!bookingId) {
      revokeVoucherUrl();
      revokeInvoiceUrl();
      setVoucherPdfUrl(null);
      setInvoicePdfUrl(null);
      setVoucherLoading(false);
      setInvoiceLoading(false);
      return undefined;
    }

    let cancelled = false;
    setVoucherLoading(true);
    setInvoiceLoading(true);

    (async () => {
      try {
        const voucherBlob = await fetchHallBookingConfirmationPdfBlob(bookingId);
        if (cancelled) return;
        const voucherUrl = URL.createObjectURL(voucherBlob);
        revokeVoucherUrl();
        voucherUrlRef.current = voucherUrl;
        setVoucherPdfUrl(voucherUrl);
      } catch {
        if (!cancelled) setVoucherPdfUrl(null);
      } finally {
        if (!cancelled) setVoucherLoading(false);
      }
    })();

    (async () => {
      try {
        const invoiceBlob = await fetchHallBookingInvoicePdfBlob(bookingId);
        if (cancelled) return;
        const nextInvoiceUrl = URL.createObjectURL(invoiceBlob);
        revokeInvoiceUrl();
        invoiceUrlRef.current = nextInvoiceUrl;
        setInvoicePdfUrl(nextInvoiceUrl);
      } catch {
        if (!cancelled) setInvoicePdfUrl(null);
      } finally {
        if (!cancelled) setInvoiceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookingId, revokeInvoiceUrl, revokeVoucherUrl]);

  useEffect(
    () => () => {
      revokeVoucherUrl();
      revokeInvoiceUrl();
    },
    [revokeInvoiceUrl, revokeVoucherUrl],
  );

  const handleDownload = async () => {
    if (!bookingId || !activePreviewDocType) {
      onNotice?.({ message: 'Select voucher or invoice to download', severity: 'warning' });
      return;
    }
    setDocLoading?.('download');
    try {
      if (activePreviewDocType === 'voucher') {
        await downloadHallBookingConfirmation(bookingId);
        onNotice?.({ message: 'Confirmation voucher downloaded', severity: 'success' });
      } else {
        await downloadHallBookingInvoice(bookingId);
        onNotice?.({ message: 'Invoice downloaded', severity: 'success' });
      }
    } catch (err) {
      onNotice?.({ message: err?.userMessage || err?.message || 'Download failed', severity: 'error' });
    } finally {
      setDocLoading?.(null);
    }
  };

  const handleSendEmail = async () => {
    if (!bookingId) return;
    setDocLoading?.('send_email');
    try {
      const res = await sendHallBookingDocuments(bookingId, {
        send_email: true,
        send_whatsapp: false,
      });
      if (res.email_sent) {
        onNotice?.({
          message: 'Confirmation voucher and invoice sent to customer email',
          severity: 'success',
        });
      } else {
        onNotice?.({ message: res.errors?.[0] || 'Email not sent', severity: 'warning' });
      }
    } catch (err) {
      onNotice?.({ message: err?.userMessage || err?.message || 'Send failed', severity: 'error' });
    } finally {
      setDocLoading?.(null);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!bookingId) return;
    setDocLoading?.('send_whatsapp');
    try {
      const res = await sendHallBookingDocuments(bookingId, {
        send_email: false,
        send_whatsapp: true,
      });
      if (res.whatsapp_sent === true) {
        const sentDocs =
          Array.isArray(res.whatsapp_sent_documents) && res.whatsapp_sent_documents.length
            ? res.whatsapp_sent_documents.join(' and ')
            : 'voucher and invoice';
        onNotice?.({
          message: formatWhatsAppSuccessMessage(res, {
            prefix: `${sentDocs.charAt(0).toUpperCase()}${sentDocs.slice(1)} sent on WhatsApp`,
            bookingCode,
          }),
          severity: 'success',
        });
      } else if (res.whatsapp_partial_sent) {
        const sentDocs = Array.isArray(res.whatsapp_sent_documents)
          ? res.whatsapp_sent_documents.join(' and ')
          : 'some documents';
        const failedDocs = Array.isArray(res.whatsapp_failed_documents)
          ? res.whatsapp_failed_documents.join(' and ')
          : 'some documents';
        onNotice?.({
          message: `WhatsApp partially sent: ${sentDocs || 'some documents'} sent, ${failedDocs || 'some documents'} failed. ${(res.errors || []).join(' ')}`,
          severity: 'warning',
        });
      } else {
        onNotice?.({
          message: formatWhatsAppErrorMessage(res),
          severity: 'warning',
        });
      }
    } catch (err) {
      onNotice?.({ message: err?.userMessage || err?.message || 'Send failed', severity: 'error' });
    } finally {
      setDocLoading?.(null);
    }
  };

  const footerIconSx = useMemo(
    () => ({
      width: 40,
      height: 40,
      borderRadius: 1.5,
      border: `1px solid ${alpha(accent, 0.45)}`,
      color: accent,
      bgcolor: alpha(accent, isDark ? 0.12 : 0.08),
      '&:hover': {
        bgcolor: alpha(accent, isDark ? 0.22 : 0.16),
        borderColor: accent,
      },
      '&.Mui-disabled': {
        opacity: 0.45,
        borderColor: alpha(accent, 0.2),
      },
    }),
    [accent, isDark],
  );

  const whatsAppIconSx = {
    ...footerIconSx,
    color: '#25D366',
    borderColor: alpha('#25D366', 0.45),
    bgcolor: alpha('#25D366', isDark ? 0.12 : 0.08),
    '&:hover': {
      bgcolor: alpha('#25D366', isDark ? 0.2 : 0.14),
      borderColor: '#25D366',
    },
  };

  const actionBusy = Boolean(docLoading);
  const canDownload = Boolean(
    activePreviewDocType === 'voucher'
      ? voucherPdfUrl
      : activePreviewDocType === 'invoice' && (invoicePdfUrl || bookingId),
  );
  const expandedPdfUrl = expandedDocType === 'voucher' ? voucherPdfUrl : invoicePdfUrl;
  const expandedTitle = expandedDocType === 'voucher' ? voucherLabel : invoiceLabel;

  const openExpandedPreview = (docType) => {
    if (docType === 'voucher' && !voucherPdfUrl) return;
    if (docType === 'invoice' && !invoicePdfUrl) return;
    setExpandedDocType(docType);
  };

  return (
    <Box
      sx={{
        mt: 0.5,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(accent, isDark ? 0.28 : 0.22)}`,
        bgcolor: isDark ? alpha('#020617', 0.45) : alpha('#f8fafc', 0.96),
      }}
    >
      <Box sx={{ p: 1.25 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 1.2,
            justifyContent: 'flex-start',
            width: 'fit-content',
            maxWidth: '100%',
          }}
        >
          <DocumentPreviewCard
            docType="voucher"
            label={voucherLabel}
            Icon={FactCheckIcon}
            selected={selectedDownloadDocs.voucher}
            focused={previewDocFocus === 'voucher'}
            pdfUrl={voucherPdfUrl}
            loading={voucherLoading}
            isDark={isDark}
            accent={accent}
            onSelect={() => {
              setPreviewDocFocus('voucher');
              setSelectedDownloadDocs((prev) => ({ ...prev, voucher: !prev.voucher }));
            }}
            onExpand={openExpandedPreview}
          />
          <DocumentPreviewCard
            docType="invoice"
            label={invoiceLabel}
            Icon={ReceiptLongIcon}
            selected={selectedDownloadDocs.invoice}
            focused={previewDocFocus === 'invoice'}
            pdfUrl={invoicePdfUrl}
            loading={invoiceLoading}
            isDark={isDark}
            accent={accent}
            onSelect={() => {
              setPreviewDocFocus('invoice');
              setSelectedDownloadDocs((prev) => ({ ...prev, invoice: !prev.invoice }));
            }}
            onExpand={openExpandedPreview}
          />
        </Box>
        {guestName ? (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: isDark ? '#94a3b8' : '#64748b',
              fontWeight: 600,
            }}
          >
            Documents for {guestName}
            {bookingCode ? ` · ${bookingCode}` : ''}
          </Typography>
        ) : null}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={0.75}
        sx={{
          px: 1.25,
          py: 1,
          borderTop: `1px solid ${alpha(accent, isDark ? 0.18 : 0.14)}`,
          bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha('#ffffff', 0.72),
        }}
      >
        <Tooltip title="View full size">
          <span>
            <IconButton
              size="small"
              onClick={() => activePreviewDocType && openExpandedPreview(activePreviewDocType)}
              disabled={
                actionBusy
                || !activePreviewDocType
                || (activePreviewDocType === 'voucher' ? !voucherPdfUrl : !invoicePdfUrl)
              }
              aria-label="View PDF full size"
              sx={footerIconSx}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={`Download ${activePreviewDocType === 'voucher' ? voucherLabel : invoiceLabel}`}>
          <span>
            <IconButton
              size="small"
              onClick={handleDownload}
              disabled={actionBusy || !canDownload}
              aria-label="Download PDF"
              sx={footerIconSx}
            >
              {docLoading === 'download' ? <CircularProgress size={18} sx={{ color: accent }} /> : <DownloadIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {canSendEmail ? (
          <Tooltip title="Send email">
            <span>
              <IconButton
                size="small"
                onClick={handleSendEmail}
                disabled={actionBusy || !activePreviewDocType}
                aria-label="Send email"
                sx={footerIconSx}
              >
                {docLoading === 'send_email' ? <CircularProgress size={18} sx={{ color: accent }} /> : <EmailIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
        {canSendWhatsApp ? (
          <Tooltip title="Send WhatsApp">
            <span>
              <IconButton
                size="small"
                onClick={handleSendWhatsApp}
                disabled={actionBusy || !activePreviewDocType}
                aria-label="Send WhatsApp"
                sx={whatsAppIconSx}
              >
                {docLoading === 'send_whatsapp' ? (
                  <CircularProgress size={18} sx={{ color: '#25D366' }} />
                ) : (
                  <WhatsAppIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Stack>

      <Dialog
        open={Boolean(expandedDocType && expandedPdfUrl)}
        onClose={() => setExpandedDocType(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, py: 1.5 }}>
          {expandedTitle} — Preview
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: isDark ? '#0b0f17' : '#e8edf3',
            p: 0,
            maxHeight: { xs: '75vh', sm: '80vh' },
            overflow: 'auto',
          }}
        >
          {expandedPdfUrl ? (
            <Box sx={{ p: 1 }}>
              <PdfFitPreview pdfUrl={expandedPdfUrl} accentColor={accent} />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button onClick={() => setExpandedDocType(null)} size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
